import { put, select, call } from "redux-saga/effects"
import * as actions from "../action_creators"
import {
    getProfile,
    getProfileId,
    getMyRunningTimeEntries,
    getCurrentInterval,
    getCurrentIncrement,
    getTeamDashboardId,
    getState,
} from "../selectors"
import * as client from "../../api/client"
import * as fmt from "../../util/fmt_utils"
import { getDefaultTimespan } from "../../util/date_utils"
import { makeToast } from "../../util/toast_util"

export function* handleFetchTimeEntries({ params }) {
    if (!params) {
        return
    }
    // TODO: Multiteam
    const { start, end, teamId } = params
    let ownerId = undefined
    if (!teamId) {
        ownerId = yield select(getProfileId)
    }
    const queryParams = ownerId
        ? { startsBy: start, startedBefore: end, ownerId }
        : { startsBy: start, startedBefore: end, teamId }

    try {
        const resp = yield call(client.getTimeRecords, queryParams)
        if (resp === undefined) return
        yield put(actions.receiveTimeEntries(resp))
    } catch (e) {
        const errorMsg = "Failed to fetch time entries"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
    }
}

export function* handleFetchRunningTimeEntries({ params }) {
    if (!params) {
        return
    }
    const { teamId } = params
    let ownerId = undefined
    if (!teamId) {
        ownerId = yield select(getProfileId)
    }
    const queryParams = ownerId
        ? { openOnly: true, ownerId }
        : { openOnly: true, teamId }

    try {
        const resp = yield call(client.getTimeRecords, queryParams)
        if (resp === undefined) return
        yield put(actions.mergeTimeEntries(resp))
        yield call(computeRunningTimeEntries)
    } catch (e) {
        const errorMsg = "Failed to fetch running time entries"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
    }
}

export function* computeRunningTimeEntries() {
    const {
        timeEntries: { items, sort },
        profile: { id: userId },
    } = yield select(getState)

    const timeIds = sort.reduce((acc, timeEntry) => {
        const isRunning = items[timeEntry] && items[timeEntry].endAt === 0
        const isSameUser = items[timeEntry].userId === userId
        return isRunning && isSameUser ? [...acc, timeEntry] : acc
    }, [])

    const meta = { userId, timeIds }
    yield put(actions.setRunningTimeEntryIds(meta.userId, meta.timeIds))
}

export function* handleCreateTimeEntry({ timeEntry }) {
    // if there are running entries and we're creating a running entry,
    // stop the existing running entries
    const runningEntries = yield select(getMyRunningTimeEntries)
    if (!timeEntry.endAt) {
        yield runningEntries.map(runningEntry =>
            call(handleUpdateTimeEntry, {
                timeEntry: {
                    ...runningEntry,
                    endAt: Date.now(),
                },
            }),
        )
    }

    // if any new tags are present, call handleCreateNewTags
    if (timeEntry.newTags && timeEntry.newTags.length > 0) {
        yield call(handleCreateNewTags, { timeEntry })
    }

    // create the new entry
    try {
        const resp = yield call(
            client.postTimeRecord,
            fmt.convertTimeEntryToUnix(timeEntry),
        )
        yield put(actions.receiveTimeEntry(resp))
        // update recent tag combo array
        yield put(
            actions.addRecentTagCombo({ id: resp.id, tags: timeEntry.tags }),
        )
    } catch (e) {
        yield call(
            makeToast,
            "Something went wrong creating the new time entry",
            "warning",
        )
        return
    }

    // navigate the UI to enclosing span:
    // 1. get current interval and increment
    const interval = yield select(getCurrentInterval)
    const increment = yield select(getCurrentIncrement)

    // 2. create a span with timeEntry.startAt as startAt
    const span = getDefaultTimespan(interval, new Date(timeEntry.startAt))

    // 3. set current range using new span
    yield put(actions.setDashboardRange({ ...span, interval, increment }))
}

export function* handleUpdateTimeEntry({ timeEntry }) {
    // if any new tags are present, call handleCreateNewTags
    if (timeEntry.newTags && timeEntry.newTags.length > 0) {
        yield call(handleCreateNewTags, { timeEntry })
    }

    try {
        const resp = yield call(
            client.putTimeRecord,
            fmt.convertTimeEntryToUnix(timeEntry),
        )
        yield put(actions.receiveTimeEntry(resp))
    } catch (e) {
        yield call(
            makeToast,
            "Something went wrong updating the time entry",
            "warning",
        )
        return
    }
}

export function* handleCreateNewTags({ timeEntry }) {
    const { newTags } = timeEntry
    const { personalTeamId, memberships } = yield select(getProfile)
    const currentTeamId = yield select(getTeamDashboardId)

    // determine if the tags should be team or personal tags
    const currentTeamMembership = memberships[currentTeamId]
    const isAdmin =
        currentTeamMembership && currentTeamMembership.accessLevel === "Admin"
    const teamId = isAdmin ? currentTeamId : personalTeamId

    for (let i = 0; i < newTags.length; i++) {
        const tag = { ...newTags[i], teamId }

        try {
            const resp = yield call(client.postTag, tag)
            timeEntry.tags = [...timeEntry.tags, resp.id] // add the new tag's returned ID to timeEntry.tags
            yield put(actions.receiveTag(resp))
        } catch (e) {
            yield call(
                makeToast,
                "Something went wrong creating new tags",
                "warning",
            )
            return
        }
    }
}

export function* handleDeleteTimeEntry({ timeEntry }) {
    try {
        yield call(client.deleteTimeRecord, timeEntry)
        yield put(actions.removeTimeEntry(timeEntry))
    } catch (e) {
        makeToast("Something went wrong deleting the time entry", "warning")
        return
    }
}

export function* downloadCsv({ params }) {
    let respText
    try {
        const resp = yield call(client.getTimeEntriesReportCSV, params)
        if (resp === undefined) return
        respText = yield resp.text()
    } catch (e) {
        const errorMsg = "Failed to Download Report"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
        return
    }

    const link = document.createElement("a")
    const csvContent =
        "data:text/csv;charset=utf-8," + encodeURIComponent(respText)

    link.setAttribute("href", csvContent)
    link.setAttribute("download", "shoutbase-time-entries.csv")
    document.body.appendChild(link)

    yield link.click()
}

export function* pushReportToZapier({ params }) {
    try {
        yield call(client.getZapierReport, params)

        const successMsg = "Report has been sent to Zapier"
        const ind = "success"
        yield call(makeToast, successMsg, ind)
    } catch (e) {
        const errorMsg = "Failed to send Report to Zapier"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
        return
    }
}



// WEBPACK FOOTER //
// ./src/redux/sagas/time_entries_sagas.js